// HQ click-to-WhatsApp registry. The pre-filled message is what Meta Ads Manager should put in the first chat.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { EmptyState, Loader, Modal, Spinner, WelcomeBar } from '../components/ui';

function createdId(data) {
  if (!data || typeof data !== 'object') return null;
  return data.id ?? data.ad_campaign?.id ?? data.campaign?.id ?? null;
}

function prefillInfo(data) {
  if (!data || typeof data === 'string') return { text: data || '', cta_label: '', cta_number: '' };
  return { text: data.text || '', cta_label: data.cta_label || '', cta_number: data.cta_number || '' };
}

function AdForm({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [branches, setBranches] = useState([]);
  const [mainNumber, setMainNumber] = useState('');
  const [cta, setCta] = useState('main');
  const [allBranches, setAllBranches] = useState(true);
  const [picked, setPicked] = useState({});
  const [home, setHome] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.branches().then((rows) => setBranches(Array.isArray(rows) ? rows : [])).catch(() => setBranches([]));
    api
      .listChannels()
      .then((data) => {
        const rows = Array.isArray(data) ? data : data.channels || [];
        const main = rows.find((row) => row.is_main && row.channel === 'whatsapp');
        setMainNumber(main?.display_number || '');
      })
      .catch(() => setMainNumber(''));
  }, []);

  const branchNumbers = branches.filter((branch) => branch.own_number);
  const chosen = branches.filter((branch) => picked[branch.franchise_id]);
  const onMain = cta === 'main';

  const toggleBranch = (id) => {
    setPicked((current) => {
      const next = { ...current, [id]: !current[id] };
      if (!next[id] && String(home) === String(id)) setHome('');
      return next;
    });
  };

  const save = async () => {
    const payload = { name: name.trim(), code: code.trim(), knowledge_note: note.trim() };
    if (!onMain) {
      payload.cta_franchise_id = Number(cta);
    } else if (!allBranches) {
      const ids = chosen.map((branch) => branch.franchise_id);
      if (!ids.length) {
        toast.error('Pick at least one branch');
        return;
      }
      payload.branch_ids = ids;
      if (home) payload.default_branch = Number(home);
    }
    setBusy(true);
    try {
      const created = await api.createAdCampaign(payload);
      toast.success('Saved');
      const id = createdId(created);
      let info = { text: '', cta_label: '', cta_number: '' };
      if (id != null) {
        try {
          info = prefillInfo(await api.adPrefill(id));
        } catch (e) {
          toast.error(e.message);
        }
      }
      onCreated(info);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New ad campaign" onClose={onClose} size="wide">
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bridal trial" />
        </label>
        <label className="de-field">
          <span className="de-label">Code (optional)</span>
          <input className="de-input de-input--bordered" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Leave blank for NAT-0001" />
        </label>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Knowledge note</span>
          <textarea
            className="de-textarea"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="The facts the AI must use: price, what is included, terms, validity."
          />
        </label>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Ad button opens</span>
          <select className="de-input de-input--bordered" value={cta} onChange={(e) => setCta(e.target.value)}>
            <option value="main">Main WhatsApp{mainNumber ? ` · ${mainNumber}` : ''}</option>
            {branchNumbers.map((branch) => (
              <option key={branch.franchise_id} value={String(branch.franchise_id)}>
                {branch.label} · {branch.own_number}
              </option>
            ))}
          </select>
        </label>
        {onMain ? (
          <div className="de-field" style={{ gridColumn: '1 / -1' }}>
            <span className="de-label">Offer is valid at</span>
            <label className="de-check">
              <input type="checkbox" checked={allBranches} onChange={(e) => setAllBranches(e.target.checked)} />
              All branches
            </label>
            {allBranches ? null : (
              <div style={{ maxHeight: 160, overflow: 'auto', marginTop: 8 }}>
                {branches.map((branch) => (
                  <label key={branch.franchise_id} className="de-check" style={{ display: 'flex', marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(picked[branch.franchise_id])}
                      onChange={() => toggleBranch(branch.franchise_id)}
                    />
                    {branch.label}
                  </label>
                ))}
              </div>
            )}
            {!allBranches && chosen.length > 1 ? (
              <label className="de-field" style={{ marginTop: 10 }}>
                <span className="de-label">Put a new chat on</span>
                <select className="de-input de-input--bordered" value={home} onChange={(e) => setHome(e.target.value)}>
                  <option value="">Let the customer choose</option>
                  {chosen.map((branch) => (
                    <option key={branch.franchise_id} value={String(branch.franchise_id)}>
                      {branch.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        ) : (
          <p className="de-small de-muted" style={{ gridColumn: '1 / -1', margin: 0 }}>
            The ad opens that branch’s WhatsApp. The offer is only for that branch, and only on that number.
          </p>
        )}
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={busy || !name.trim()} onClick={save}>
          {busy ? <Spinner light /> : null}
          Save
        </button>
      </div>
    </Modal>
  );
}

export default function AdCampaigns() {
  const [rows, setRows] = useState(null);
  const [unlinked, setUnlinked] = useState([]);
  const [unlinkedFailed, setUnlinkedFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [linkIds, setLinkIds] = useState({});
  const [linking, setLinking] = useState(null);
  const [mockFor, setMockFor] = useState(null);
  const [mockPhone, setMockPhone] = useState('');
  const [mockBusy, setMockBusy] = useState(false);
  const [mockResult, setMockResult] = useState(null);

  const loadCampaigns = () =>
    api
      .listAdCampaigns()
      .then(setRows)
      .catch((e) => {
        toast.error(e.message);
        setRows((current) => current ?? []);
      });

  const loadUnlinked = () =>
    api
      .listUnlinkedAds()
      .then((data) => {
        setUnlinkedFailed(false);
        setUnlinked(data);
      })
      .catch((e) => {
        toast.error(e.message);
        setUnlinkedFailed(true);
        setUnlinked([]);
      });

  const load = () => {
    loadCampaigns();
    loadUnlinked();
  };

  useEffect(() => {
    load();
  }, []);

  const mockClick = async () => {
    if (!mockFor) return;
    setMockBusy(true);
    setMockResult(null);
    try {
      const result = await api.mockAdClick(mockFor.id, { phone: mockPhone.trim() });
      setMockResult(result);
      toast.success(result.matched ? 'Ad click matched this campaign' : 'Click saved, campaign did not match');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMockBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prefill.text || '');
      toast.success('Copied');
    } catch (e) {
      toast.error(e.message || 'Could not copy');
    }
  };

  const link = async (row) => {
    const raw = String(linkIds[row.id] || '').trim();
    if (!raw) {
      toast.error('Enter a campaign id');
      return;
    }
    const adCampaignId = /^\d+$/.test(raw) ? Number(raw) : raw;
    setLinking(row.id);
    try {
      await api.linkUnlinkedAd(row.id, adCampaignId);
      toast.success('Linked');
      setLinkIds((current) => ({ ...current, [row.id]: '' }));
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="de-fade">
      <WelcomeBar title="Ad campaigns" sub="Click-to-WhatsApp codes. The pre-filled message is what the ad should send as the first chat." />
      <div className="de-panel" style={{ marginBottom: 16 }}>
        <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div className="de-panel-title" style={{ margin: 0 }}>
            Ad campaigns
          </div>
          <button type="button" className="de-btn de-btn--primary" onClick={() => setCreating(true)}>
            New ad campaign
          </button>
        </div>
        {prefill?.text ? (
          <div className="de-callout" style={{ marginBottom: 14 }}>
            <div className="de-small de-muted">Pre-filled message</div>
            <div style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>{prefill.text}</div>
            {prefill.cta_number || prefill.cta_label ? (
              <div className="de-small" style={{ marginBottom: 8 }}>
                In Meta, the ad button opens {prefill.cta_label || 'this number'}
                {prefill.cta_number ? ` · ${prefill.cta_number}` : ''}.
              </div>
            ) : null}
            <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={copy}>
              Copy
            </button>
          </div>
        ) : null}
        {!rows ? (
          <Loader />
        ) : rows.length ? (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Opens on</th>
                    <th>Branches</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Test</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="de-td-strong">{row.code || '—'}</td>
                      <td>{row.name || '—'}</td>
                      <td>
                        {row.cta_label || 'Main WhatsApp'}
                        {row.cta_number ? ` · ${row.cta_number}` : ''}
                      </td>
                      <td>{row.branch_scope === 'selected' ? (row.branch_labels || []).join(', ') || '—' : 'All branches'}</td>
                      <td>
                        <span className="de-pill de-pill--sm de-pill--muted">{row.status || '—'}</span>
                      </td>
                      <td className="de-td-right">
                        <button
                          type="button"
                          className="de-btn de-btn--outline de-btn--sm"
                          onClick={() => {
                            setMockFor(row);
                            setMockResult(null);
                          }}
                        >
                          Mock click
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState icon="📣" title="No ad campaigns yet" body="Add a code and the facts the AI must use when someone replies." />
        )}
      </div>
      <div className="de-panel">
        <div className="de-panel-title">Unlinked ads</div>
        {unlinkedFailed ? (
          <div className="de-small de-muted">Unlinked ads could not be loaded.</div>
        ) : unlinked.length ? (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Ad id</th>
                    <th>Headline</th>
                    <th style={{ textAlign: 'right' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {unlinked.map((row) => (
                    <tr key={row.id}>
                      <td className="de-td-strong">{row.ad_id || row.id}</td>
                      <td>{row.headline || '—'}</td>
                      <td className="de-td-right">
                        <div className="de-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <input
                            className="de-input de-input--bordered"
                            style={{ width: 140 }}
                            value={linkIds[row.id] || ''}
                            onChange={(e) => setLinkIds((current) => ({ ...current, [row.id]: e.target.value }))}
                            placeholder="Campaign id"
                          />
                          <button
                            type="button"
                            className="de-btn de-btn--outline de-btn--sm"
                            disabled={linking === row.id}
                            onClick={() => link(row)}
                          >
                            {linking === row.id ? <Spinner /> : null}
                            Link
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="de-small de-muted">No unlinked ads</div>
        )}
      </div>
      {mockFor ? (
        <Modal title={`Mock ad click · ${mockFor.code || mockFor.name}`} onClose={() => setMockFor(null)}>
          <p className="de-small de-muted" style={{ marginTop: 0 }}>
            Sends the ad’s first message to this WhatsApp number, labels the lead, and lets the AI reply for real.
          </p>
          <label className="de-field">
            <span className="de-label">Mobile number</span>
            <input
              className="de-input de-input--bordered"
              value={mockPhone}
              onChange={(e) => setMockPhone(e.target.value)}
              placeholder="WhatsApp number to message"
            />
          </label>
          {mockResult ? (
            <div className="de-callout" style={{ marginTop: 14 }}>
              <div className="de-small de-muted">{mockResult.matched ? 'Matched this campaign' : 'Not matched to this campaign'}</div>
              <div style={{ margin: '8px 0' }}>
                Lead: {mockResult.lead?.entry_type || '—'} · ad {mockResult.lead?.ad_id || '—'}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{mockResult.reply || 'The AI did not reply.'}</div>
              {mockResult.reply_status ? <div className="de-small de-muted">WhatsApp: {mockResult.reply_status}</div> : null}
            </div>
          ) : null}
          <div className="de-form-actions" style={{ marginTop: 18 }}>
            <button type="button" className="de-btn de-btn--outline" onClick={() => setMockFor(null)}>
              Close
            </button>
            <button type="button" className="de-btn de-btn--primary" disabled={mockBusy || !mockPhone.trim()} onClick={mockClick}>
              {mockBusy ? <Spinner light /> : null}
              Send mock click
            </button>
          </div>
        </Modal>
      ) : null}
      {creating ? (
        <AdForm
          onClose={() => setCreating(false)}
          onCreated={(info) => {
            setCreating(false);
            if (info?.text) setPrefill(info);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
